import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, Gavel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMySanctions } from '../../hooks/useSanction';
import { formatDateKST } from '../../utils/dateUtils';

const SanctionNoticePage: React.FC = () => {
  const navigate = useNavigate();
  const { sanctions, loading, error } = useMySanctions();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            이용 제한 안내
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Gavel sx={{ color: 'error.main' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'error.main' }}>
            적용된 제재 내역
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mb: 3 }}>
          이의가 있으시면 이의제기를 통해 재검토를 요청하실 수 있습니다.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && sanctions.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            적용된 제재가 없습니다.
          </Typography>
        )}

        {sanctions.map((s) => (
          <Paper
            key={s.reportId}
            elevation={0}
            sx={{
              p: 2.5, mb: 2, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={s.sanctionTypeTitle}
                    size="small"
                    color={s.sanctionType === 'BAN' ? 'error' : s.sanctionType === 'SUSPENSION' ? 'warning' : 'default'}
                    sx={{ fontSize: '0.8rem', height: 24 }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', mb: 0.5 }}>
                  {s.categoryTitle}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {formatDateKST(s.createdAt)}
                </Typography>
              </Box>
              {s.hasAppealed ? (
                <Chip label="이의제기 완료" size="small" variant="outlined" sx={{ fontSize: '0.8rem' }} />
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => navigate(`/appeal?sanctionId=${s.reportId}&sanctionType=${s.sanctionType}`)}
                  sx={{ fontSize: '0.85rem', fontWeight: 600 }}
                >
                  이의제기하기
                </Button>
              )}
            </Box>
          </Paper>
        ))}

        <Button
          variant="outlined"
          fullWidth
          onClick={() => navigate('/appeal/list')}
          sx={{ mt: 1, mb: 2, fontSize: '0.9rem', fontWeight: 600 }}
        >
          이의제기 내역 보기
        </Button>

        <Button
          variant="text"
          fullWidth
          onClick={() => navigate('/support')}
          sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
        >
          고객센터 홈으로
        </Button>
      </Container>
    </Box>
  );
};

export default SanctionNoticePage;
